import { useEffect, useRef, useState } from "react";
import { X, Camera, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

// Live webcam capture, layered on top of LocalPhotosModal — lets a snapshot land straight in
// photoFiles without the old "photograph separately → find the file → upload" detour (see
// App.jsx handlePhotoFilesSelected for the file-picker sibling of this flow; both produce the
// same { name, base64 } shape so nothing downstream needs to know which one was used).
export default function CameraCaptureModal({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [error, setError] = useState("");

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const startStream = async (preferredDeviceId) => {
    setError("");
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: preferredDeviceId ? { deviceId: { exact: preferredDeviceId } } : true,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      // Device labels are blank until a stream has actually been granted once — this is why the
      // device list is (re-)populated only after a successful getUserMedia, not on mount.
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter(d => d.kind === "videoinput");
      setDevices(cams);
      const activeId = stream.getVideoTracks()[0]?.getSettings()?.deviceId || preferredDeviceId || cams[0]?.deviceId || null;
      setDeviceId(activeId);
    } catch (e) {
      setError(e.name === "NotFoundError" ? "Камера не найдена" : e.name === "NotAllowedError" ? "Доступ к камере не разрешён" : `Не удалось подключиться к камере: ${e.message}`);
    }
  };

  useEffect(() => {
    startStream(null);
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeviceChange = (id) => {
    startStream(id);
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const base64 = canvas.toDataURL("image/jpeg", 0.9);
    const stamp = new Date().toISOString().replace("T", " ").slice(0, 19).replace(/:/g, "-");
    onCapture({ name: `Снимок ${stamp}.jpg`, base64 });
    // One click = one photo = done — no more "stay open for a burst of shots"; if another photo
    // is needed, reopening this modal is one click away from the list.
    handleClose();
  };

  const handleClose = () => {
    stopStream();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[400] flex items-center justify-center p-4"
      onMouseDown={e => e.target === e.currentTarget && handleClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col" style={{ maxWidth: 640 }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <span className="text-base font-bold text-slate-900">Снять камерой</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-3">
          {devices.length > 1 && (
            <select
              className="h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 text-sm"
              value={deviceId || ""}
              onChange={e => handleDeviceChange(e.target.value)}
            >
              {devices.map(d => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || "Камера"}</option>
              ))}
            </select>
          )}

          <div className="relative rounded-xl overflow-hidden bg-slate-900 aspect-video flex items-center justify-center">
            {error ? (
              <div className="text-sm text-slate-300 text-center px-6">{error}</div>
            ) : (
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-contain" />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100 flex-shrink-0">
          <Button type="button" variant="ghost" onClick={handleClose}>Готово</Button>
          {error ? (
            <Button type="button" className="gap-1.5" onClick={() => startStream(deviceId)}>
              <RefreshCw className="h-3.5 w-3.5" />
              Повторить
            </Button>
          ) : (
            <Button type="button" className="gap-1.5" onClick={handleCapture}>
              <Camera className="h-3.5 w-3.5" />
              Снять
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
